/* assets/sonno-variants.js
   Sonno style variant tabs (Colour/Size/Storage/Headboard/Base)
   Reads:
   - <script type="application/json" id="SonnoProductJSON-{{ section.id }}"> {{ product | json }}
   - <script type="application/json" id="SonnoMetaJSON-{{ section.id }}"> { summary_icons, colour, size, storage, headboard, base }
*/

(function () {
  "use strict";

  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  function safeJsonParse(str, fallback = null) {
    try { return JSON.parse(str); } catch (e) { return fallback; }
  }

  function esc(str) {
    return String(str ?? "")
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#039;");
  }

  function getVariantIdFromUrl() {
    try {
      const url = new URL(window.location.href);
      const v = url.searchParams.get("variant");
      return v ? String(v) : null;
    } catch (e) { return null; }
  }

  function setVariantIdInUrl(variantId) {
    try {
      const url = new URL(window.location.href);
      url.searchParams.set("variant", String(variantId));
      window.history.replaceState({}, "", url.toString());
    } catch (e) {}
  }

  function normalizeOptName(s) {
    return String(s || "").trim().toLowerCase();
  }

  // Find option index based on product.options names
  function detectOptionIndex(product, key, meta) {
  const opts = (product?.options || []).map(normalizeOptName);

  // ✅ 1) If JSON tells exact option name, use it (most reliable)
  const metaOpt = meta?.[key]?.option_name;
  if (metaOpt) {
    const wantExact = normalizeOptName(metaOpt);
    const exactIdx = opts.findIndex((n) => n === wantExact);
    if (exactIdx >= 0) return exactIdx;
  }

  // ✅ 2) fallback: synonyms (your old logic)
  const want = normalizeOptName(key);
  const synonyms = {
    colour: ["colour", "color", "fabric", "colourway"],
    size: ["size"],
    storage: ["storage", "drawers", "drawer", "ottoman"],
    headboard: ["headboard"],
    base: ["base", "base type", "base_type"]
  };

  const list = synonyms[want] || [want];
  for (let i = 0; i < opts.length; i++) {
    for (const s of list) {
      if (opts[i] === s || opts[i].includes(s)) return i;
    }
  }
  return -1;
}

  function buildSelectionFromVariant(product, variant) {
    const n = Math.max((product?.options || []).length, 5); // ensure 5 slots
    const sel = new Array(n).fill("");
    if (!variant) return sel;

    for (let i = 0; i < n; i++) {
      const k = "option" + (i + 1);
      sel[i] = variant?.[k] || "";
    }
    return sel;
  }

  function findVariantBySelection(product, selection) {
    const variants = product?.variants || [];
    if (!variants.length) return null;

    return variants.find((v) => {
      for (let i = 0; i < selection.length; i++) {
        const k = "option" + (i + 1);
        if (selection[i] && String(v?.[k] || "") !== String(selection[i])) return false;
      }
      return true;
    }) || null;
  }

  function getAddToCartElements(root) {
    const form =
      $("#SonnoProductForm", root) ||
      $('form[action*="/cart/add"]', root) ||
      $("product-form form", root);

    const idInput =
      (form && $('input[name="id"]', form)) ||
      $("#SonnoVariantId", root) ||
      $('input[name="id"]', root);

    const atc =
      (form && (form.querySelector('button[type="submit"]') || form.querySelector('[name="add"]'))) ||
      root.querySelector('button[type="submit"][name="add"]') ||
      root.querySelector('button[type="submit"]');

    return { form, idInput, atc };
  }

  function setATCState(atcBtn, variant) {
    if (!atcBtn) return;
    const available = !!variant?.available;
    atcBtn.disabled = !available;
    atcBtn.textContent = available ? "Add to Basket" : "Unavailable";
  }

  // -----------------------------
  // Rendering
  // -----------------------------
  const TAB_ORDER = ["colour", "size", "storage", "headboard", "base"];

  function buildTabButton(key, meta, selectedLabel, isActive) {
    const t = meta?.summary_icons?.[key] || {};
    const label = t.label || key.charAt(0).toUpperCase() + key.slice(1);
    const icon = t.icon || "";

    return `
      <button type="button" class="sonno-tab ${isActive ? "is-active" : ""}" data-tab="${esc(key)}">
        ${icon ? `<img class="sonno-tab-ico" src="${esc(icon)}" alt="" loading="lazy">` : ""}
        <b>${esc(label)}</b>
        <small class="sonno-tab-sub">${esc(selectedLabel || "")}</small>
      </button>
    `;
  }

  function renderPanelGrid(key, cfg, selection, optionIndex) {
    const items = Array.isArray(cfg?.items) ? cfg.items : [];
    const style = cfg?.style || "buttons";

    if (!items.length) return `<div class="sonno-empty">No options found.</div>`;

    if (style === "image_cards") {
      return `
        <div class="sonno-grid">
          ${items.map((it) => {
            const title = it?.value || "";
            const img = it?.card_image || it?.swatch_image || it?.thumb_image || it?.image || it?.thumb || "";
            const alt = it?.alt || title || "";
            const isSelected = optionIndex >= 0 && selection[optionIndex] === title;

            return `
              <button type="button"
                class="sonno-card ${isSelected ? "is-selected" : ""}"
                data-tab="${esc(key)}"
                data-value="${esc(title)}">
                <div class="sonno-card-img">
                  ${img ? `<img src="${esc(img)}" alt="${esc(alt)}" loading="lazy">` : ""}
                </div>
                <div class="sonno-card-title">${esc(title)}</div>
              </button>
            `;
          }).join("")}
        </div>
      `;
    }

    return `
      <div class="sonno-list">
        ${items.map((it) => {
          const title = it?.value || "";
          const icon = it?.icon || "";
          const priceDelta = it?.price_delta || it?.price || "";
          const isSelected = optionIndex >= 0 && selection[optionIndex] === title;

          return `
            <button type="button"
              class="sonno-btn ${isSelected ? "is-selected" : ""}"
              data-tab="${esc(key)}"
              data-value="${esc(title)}">
              ${icon ? `<img class="sonno-ico" src="${esc(icon)}" alt="" loading="lazy">` : ""}
              <span>${esc(title)}</span>
              ${priceDelta ? `<em>${esc(priceDelta)}</em>` : ""}
            </button>
          `;
        }).join("")}
      </div>
    `;
  }

   function tabSelectedLabel(key, meta, selection, idx){
      if (idx < 0) return "";
      const v = selection[idx] || "";
      const items = Array.isArray(meta?.[key]?.items) ? meta[key].items : [];
      const exists = items.some(it => String(it?.value || "") === String(v));
      return exists ? v : "";
    }

  function renderUI(root, meta, state) {
    const tabsWrap = $(".sonno-tabs", root);
    const panelsWrap = $(".sonno-panels", root);
    if (!tabsWrap || !panelsWrap) return;

    const activeTab = state.activeTab || TAB_ORDER[0];

    tabsWrap.innerHTML = TAB_ORDER.map((key) => {
      const idx = state.optionIndexByKey[key];
      const selectedLabel = tabSelectedLabel(
  key,
  meta,
  state.selection,
  idx
);
      return buildTabButton(key, meta, selectedLabel, key === activeTab);
    }).join("");

    panelsWrap.innerHTML = TAB_ORDER.map((key) => {
      const cfg = meta?.[key] || {};
      const optionIndex = state.optionIndexByKey[key];
      const panelInner = renderPanelGrid(key, cfg, state.selection, optionIndex);

      return `
        <div class="sonno-panel ${key === activeTab ? "is-active" : ""}" data-panel="${esc(key)}">
          ${panelInner}
        </div>
      `;
    }).join("");
  }

  // -----------------------------
  // Main init per section
  // -----------------------------
  function initWithScripts(sectionEl, productScript, metaScript) {
    const product = safeJsonParse(productScript.textContent, null);
    const meta = safeJsonParse(metaScript.textContent, null);

    if (!product || !meta) {
      console.warn("[SONNO] Missing product/meta JSON");
      return;
    }

    // 1) detect indices
    const optionIndexByKey = {};
    TAB_ORDER.forEach((k) => (optionIndexByKey[k] = detectOptionIndex(product, k, meta)));

    // 2) fallback mapping (VERY IMPORTANT)
    // if detect fails, map in order: colour->0, size->1, storage->2, headboard->3, base->4
    TAB_ORDER.forEach((k, pos) => {
      if (optionIndexByKey[k] < 0) optionIndexByKey[k] = pos;
    });

    // initial variant
    const urlVariantId = getVariantIdFromUrl();
    let currentVariant =
      (urlVariantId && product.variants.find((v) => String(v.id) === String(urlVariantId))) ||
      product.selected_or_first_available_variant ||
      product.variants?.[0] ||
      null;

    const selection = buildSelectionFromVariant(product, currentVariant);

    // 3) auto-pick first item for each tab if selection blank
    TAB_ORDER.forEach((key) => {
      const idx = optionIndexByKey[key];
      const cfg = meta?.[key] || {};
      const items = Array.isArray(cfg?.items) ? cfg.items : [];
      if (!items.length) return;

      if (!selection[idx]) {
        const firstVal = items[0]?.value || "";
        if (firstVal) selection[idx] = firstVal;
      }
    });

    // 4) match variant after auto-pick
    const matched = findVariantBySelection(product, selection);
    if (matched) currentVariant = matched;

    const state = {
      activeTab: TAB_ORDER[0],
      optionIndexByKey,
      selection,
      currentVariant
    };

    // Render UI
    renderUI(sectionEl, meta, state);

   

    // ATC wiring
    const { idInput, atc } = getAddToCartElements(document);
    if (idInput && state.currentVariant?.id) idInput.value = state.currentVariant.id;
    setATCState(atc, state.currentVariant);

    // Events
    sectionEl.addEventListener("click", (e) => {
      const tabBtn = e.target.closest(".sonno-tab");
      if (tabBtn) {
        const key = tabBtn.getAttribute("data-tab");
        if (!key) return;
        state.activeTab = key;
        renderUI(sectionEl, meta, state);
        return;
      }

      const optBtn = e.target.closest(".sonno-card, .sonno-btn");
      if (optBtn) {
        const key = optBtn.getAttribute("data-tab");
        const value = optBtn.getAttribute("data-value");
        if (!key || !value) return;

        const idx = state.optionIndexByKey[key]; // now never -1
        state.selection[idx] = value;

        const v = findVariantBySelection(product, state.selection);
        if (v) state.currentVariant = v;

        if (state.currentVariant?.id) {
          setVariantIdInUrl(state.currentVariant.id);
          if (idInput) idInput.value = state.currentVariant.id;
        }

        setATCState(atc, state.currentVariant);
        renderUI(sectionEl, meta, state);
      }
    });
  }

  function initSection(sectionEl) {
    const sectionId = sectionEl.getAttribute("data-section-id") || "";
    const productScript = $(`#SonnoProductJSON-${CSS.escape(sectionId)}`, document);
    const metaScript = $(`#SonnoMetaJSON-${CSS.escape(sectionId)}`, document);

    if (!productScript || !metaScript) {
      // fallback: find inside section
      const p2 = sectionEl.querySelector('script[id^="SonnoProductJSON"]');
      const m2 = sectionEl.querySelector('script[id^="SonnoMetaJSON"]');
      if (!p2 || !m2) return;
      return initWithScripts(sectionEl, p2, m2);
    }

    return initWithScripts(sectionEl, productScript, metaScript);
  }

  function boot() {
    const roots = $$(".sonno-opts");
    if (!roots.length) return;
    roots.forEach((r) => {
      try { initSection(r); }
      catch (e) { console.error("[SONNO] init error", e); }
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();